import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useRole } from "./RoleContext";

const STYLE_KEY = "pms_screen_style";
const ACCENT_KEY = "pms_accent_palette";

export const SCREEN_STYLES = [
  { key: "light", label: "Light", hint: "Bright background" },
  { key: "dark", label: "Dark", hint: "Easier on the eyes at night" },
  { key: "auto", label: "Auto", hint: "Follows your device setting" },
];

export const ACCENT_PALETTES = [
  { key: "indigo", label: "Indigo", swatch: "#5B4FE0" },
  { key: "ocean", label: "Ocean", swatch: "#1F6FD1" },
  { key: "lagoon", label: "Lagoon", swatch: "#0B7F79" },
  { key: "forest", label: "Forest", swatch: "#2E7D32" },
  { key: "sunset", label: "Sunset", swatch: "#C2410C" },
  { key: "berry", label: "Berry", swatch: "#BE185D" },
  { key: "graphite", label: "Graphite", swatch: "#475569" },
];

const DEFAULT_STYLE = "light";
const DEFAULT_ACCENT = "indigo";

function read(key, allowed, fallback) {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
  }
}

const darkQuery = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null);
const resolve = (style) => (style === "auto" ? (darkQuery()?.matches ? "dark" : "light") : style);

function applyToDocument(active, theme, accent) {
  const root = document.documentElement;
  if (active) {
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-accent", accent);
  } else {
    root.removeAttribute("data-theme");
    root.removeAttribute("data-accent");
  }
}

export function applyStoredTheme() {
  let signedIn = false;
  try {
    signedIn = Boolean(localStorage.getItem("access_token"));
  } catch {
    signedIn = false;
  }
  const style = read(STYLE_KEY, SCREEN_STYLES.map((s) => s.key), DEFAULT_STYLE);
  const accent = read(ACCENT_KEY, ACCENT_PALETTES.map((a) => a.key), DEFAULT_ACCENT);
  applyToDocument(signedIn, resolve(style), accent);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { session } = useRole();
  const [screenStyle, setScreenStyleState] = useState(() => read(STYLE_KEY, SCREEN_STYLES.map((s) => s.key), DEFAULT_STYLE));
  const [accent, setAccentState] = useState(() => read(ACCENT_KEY, ACCENT_PALETTES.map((a) => a.key), DEFAULT_ACCENT));
  const [deviceDark, setDeviceDark] = useState(() => Boolean(darkQuery()?.matches));

  useEffect(() => {
    const query = darkQuery();
    if (!query) return undefined;
    const onChange = (e) => setDeviceDark(e.matches);
    if (query.addEventListener) query.addEventListener("change", onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener("change", onChange);
      else query.removeListener(onChange);
    };
  }, []);

  const theme = screenStyle === "auto" ? (deviceDark ? "dark" : "light") : screenStyle;
  const active = Boolean(session);

  useLayoutEffect(() => {
    applyToDocument(active, theme, accent);
  }, [active, theme, accent]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STYLE_KEY) setScreenStyleState(read(STYLE_KEY, SCREEN_STYLES.map((s) => s.key), DEFAULT_STYLE));
      if (e.key === ACCENT_KEY) setAccentState(read(ACCENT_KEY, ACCENT_PALETTES.map((a) => a.key), DEFAULT_ACCENT));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setScreenStyle = useCallback((value) => {
    setScreenStyleState(value);
    write(STYLE_KEY, value);
  }, []);

  const setAccent = useCallback((value) => {
    setAccentState(value);
    write(ACCENT_KEY, value);
  }, []);

  const value = useMemo(
    () => ({ screenStyle, setScreenStyle, accent, setAccent, theme: active ? theme : "light" }),
    [screenStyle, setScreenStyle, accent, setAccent, theme, active]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
